import { useId, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from 'react'
import { Check } from 'lucide-react'

type FieldShellProps = {
  label: ReactNode
  hint?: ReactNode
  error?: string
  optional?: boolean
  className?: string
  children: (ids: { id: string; describedBy?: string; invalid: boolean }) => ReactNode
  /** Stable id so errors can link to the field (and survive re-renders). */
  id?: string
}

export function Field({ label, hint, error, optional, className = '', children, id: given }: FieldShellProps) {
  const auto = useId()
  const id = given ?? auto
  const hintId = hint ? `${id}-hint` : undefined
  const errId = error ? `${id}-error` : undefined
  const describedBy = [hintId, errId].filter(Boolean).join(' ') || undefined
  return (
    <div className={`flex min-w-0 flex-col gap-1.5 ${className}`}>
      <label htmlFor={id} className="text-sm font-semibold text-ink">
        {label}
        {optional && <span className="ml-1.5 font-normal text-ink-3">(optional)</span>}
      </label>
      {hint && (
        <p id={hintId} className="-mt-0.5 text-sm text-ink-3">
          {hint}
        </p>
      )}
      {children({ id, describedBy, invalid: !!error })}
      {error && (
        <p id={errId} className="text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

type InputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> & {
  label: ReactNode
  hint?: ReactNode
  error?: string
  optional?: boolean
  fieldId?: string
  wrapperClassName?: string
}

export function TextField({ label, hint, error, optional, fieldId, wrapperClassName, className = '', ...rest }: InputProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} id={fieldId} className={wrapperClassName}>
      {({ id, describedBy, invalid }) => (
        <input
          id={id}
          aria-describedby={describedBy}
          aria-invalid={invalid || undefined}
          className={`control ${className}`}
          {...rest}
        />
      )}
    </Field>
  )
}

type SelectProps = Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> & {
  label: ReactNode
  hint?: ReactNode
  error?: string
  optional?: boolean
  fieldId?: string
  wrapperClassName?: string
  options: { value: string; label: string }[]
  placeholder?: string
}

export function SelectField({ label, hint, error, optional, fieldId, wrapperClassName, options, placeholder, className = '', ...rest }: SelectProps) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} id={fieldId} className={wrapperClassName}>
      {({ id, describedBy, invalid }) => (
        <select id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} className={`control ${className}`} {...rest}>
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      )}
    </Field>
  )
}

export function TextAreaField({
  label,
  hint,
  error,
  optional,
  fieldId,
  wrapperClassName,
  className = '',
  ...rest
}: Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, 'id'> & { label: ReactNode; hint?: ReactNode; error?: string; optional?: boolean; fieldId?: string; wrapperClassName?: string }) {
  return (
    <Field label={label} hint={hint} error={error} optional={optional} id={fieldId} className={wrapperClassName}>
      {({ id, describedBy, invalid }) => (
        <textarea id={id} aria-describedby={describedBy} aria-invalid={invalid || undefined} className={`control min-h-28 ${className}`} {...rest} />
      )}
    </Field>
  )
}

export function Checkbox({
  checked,
  onChange,
  children,
  id,
  error,
  hint,
}: {
  checked: boolean
  onChange: (v: boolean) => void
  children: ReactNode
  id: string
  error?: string
  hint?: ReactNode
}) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="group flex cursor-pointer items-start gap-3">
        <span className="relative mt-0.5 grid size-5 shrink-0 place-items-center">
          <input
            id={id}
            type="checkbox"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-invalid={error ? true : undefined}
            aria-describedby={error ? `${id}-error` : undefined}
            className="peer size-5 cursor-pointer appearance-none rounded-[0.3rem] border border-line-strong bg-surface checked:border-brand checked:bg-brand aria-[invalid=true]:border-danger"
          />
          <Check className="pointer-events-none absolute size-3.5 text-on-brand opacity-0 peer-checked:opacity-100" strokeWidth={3} aria-hidden />
        </span>
        <span className="text-[0.95rem] text-ink">
          {children}
          {hint && <span className="mt-0.5 block text-sm text-ink-3">{hint}</span>}
        </span>
      </label>
      {error && (
        <p id={`${id}-error`} className="ml-8 text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </div>
  )
}

/** Large tappable radio cards (event choice, shirt fit, donation amount…). */
export function ChoiceCards<T extends string>({
  name,
  legend,
  value,
  onChange,
  options,
  error,
  columns = 'sm:grid-cols-3',
  hideLegend,
  fieldId,
  compact,
}: {
  fieldId?: string
  compact?: boolean
  name: string
  legend: ReactNode
  value: T | ''
  onChange: (v: T) => void
  options: { value: T; title: ReactNode; detail?: ReactNode; aside?: ReactNode; disabled?: boolean }[]
  error?: string
  columns?: string
  hideLegend?: boolean
}) {
  return (
    <fieldset id={fieldId} tabIndex={fieldId ? -1 : undefined} className="min-w-0 focus:outline-none" aria-describedby={error ? `${name}-error` : undefined}>
      <legend className={hideLegend ? 'sr-only' : 'mb-2 text-sm font-semibold text-ink'}>{legend}</legend>
      <div className={`grid grid-cols-1 gap-2.5 ${columns}`}>
        {options.map((o) => {
          const id = `${name}-${o.value}`
          const selected = value === o.value
          return (
            <label
              key={o.value}
              htmlFor={id}
              className={`relative flex min-w-0 cursor-pointer flex-col gap-1 rounded-xl border bg-surface ${compact ? 'p-2.5 sm:p-3.5' : 'p-3.5'} transition-colors has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent-bright ${
                selected ? 'border-brand ring-1 ring-brand' : 'border-line-strong hover:border-ink-3'
              } ${o.disabled ? 'cursor-not-allowed opacity-55' : ''}`}
            >
              <input
                id={id}
                type="radio"
                name={name}
                value={o.value}
                checked={selected}
                disabled={o.disabled}
                onChange={() => onChange(o.value)}
                className="sr-only"
              />
              <span className="flex items-start justify-between gap-2">
                <span className="min-w-0 font-semibold leading-snug text-ink [overflow-wrap:anywhere]">{o.title}</span>
                <span
                  className={`mt-0.5 grid size-5 shrink-0 place-items-center rounded-full border ${selected ? 'border-brand bg-brand' : 'border-line-strong'}`}
                  aria-hidden
                >
                  {selected && <Check className="size-3 text-on-brand" strokeWidth={3.5} />}
                </span>
              </span>
              {o.detail && <span className="text-sm leading-snug text-ink-2">{o.detail}</span>}
              {o.aside && <span className="mt-auto pt-1">{o.aside}</span>}
            </label>
          )
        })}
      </div>
      {error && (
        <p id={`${name}-error`} className="mt-2 text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </fieldset>
  )
}

/** Compact pill radios for sizes. */
export function SizePills({
  name,
  legend,
  sizes,
  value,
  onChange,
  error,
  sub,
  fieldId,
}: {
  fieldId?: string
  name: string
  legend: ReactNode
  sizes: string[]
  value: string
  onChange: (v: string) => void
  error?: string
  sub?: (size: string) => string | undefined
}) {
  return (
    <fieldset id={fieldId} tabIndex={fieldId ? -1 : undefined} className="min-w-0 focus:outline-none" aria-describedby={error ? `${name}-error` : undefined}>
      <legend className="sr-only">{legend}</legend>
      <div className="flex flex-wrap gap-2">
        {sizes.map((s) => {
          const id = `${name}-${s}`
          const selected = value === s
          const extra = sub?.(s)
          return (
            <label
              key={s}
              htmlFor={id}
              className={`flex min-w-[3.25rem] cursor-pointer flex-col items-center rounded-lg border px-3 py-2 text-center transition-colors has-[:focus-visible]:outline-3 has-[:focus-visible]:outline-offset-2 has-[:focus-visible]:outline-accent-bright ${
                selected ? 'border-brand bg-brand text-on-brand' : 'border-line-strong bg-surface text-ink hover:border-ink-3'
              }`}
            >
              <input id={id} type="radio" name={name} value={s} checked={selected} onChange={() => onChange(s)} className="sr-only" />
              <span className="font-semibold leading-none">{s}</span>
              {extra && <span className={`mt-1 text-[0.68rem] leading-none ${selected ? 'text-on-brand/80' : 'text-ink-3'}`}>{extra}</span>}
            </label>
          )
        })}
      </div>
      {error && (
        <p id={`${name}-error`} className="mt-2 text-sm font-medium text-danger">
          {error}
        </p>
      )}
    </fieldset>
  )
}

/** Summary of errors at the top of a step; links jump to each field. */
export function ErrorSummary({ errors, labels, id = 'error-summary' }: { errors: Record<string, string>; labels?: (key: string) => string; id?: string }) {
  const entries = Object.entries(errors)
  if (!entries.length) return null
  return (
    <div id={id} tabIndex={-1} role="alert" className="rounded-xl border border-danger/35 bg-danger-soft p-4 focus:outline-none">
      <p className="font-semibold text-ink">
        {entries.length === 1 ? 'One thing needs fixing' : `${entries.length} things need fixing`} before you continue
      </p>
      <ul className="mt-2 list-disc space-y-1 pl-5 text-[0.95rem]">
        {entries.slice(0, 8).map(([key, msg]) => (
          <li key={key}>
            <a
              href={`#f-${key.replace(/\./g, '-')}`}
              className="text-danger underline underline-offset-2"
              onClick={(e) => {
                e.preventDefault()
                const el = document.getElementById(`f-${key.replace(/\./g, '-')}`) ?? document.querySelector<HTMLElement>(`[name="${key}"]`)
                el?.focus()
                el?.scrollIntoView({ block: 'center', behavior: 'smooth' })
              }}
            >
              {labels?.(key) ? `${labels(key)}: ` : ''}
              {msg}
            </a>
          </li>
        ))}
      </ul>
    </div>
  )
}
